import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import api from '../api';

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const checkedRef = useRef(false);

  const checkAuth = useCallback(async () => {
    const token = localStorage.getItem('vw_token');
    if (!token) { setLoading(false); return; }
    try {
      const res = await api.get('/auth/me');
      setUser(res.data);
    } catch {
      localStorage.removeItem('vw_token');
      localStorage.removeItem('vw_user');
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (window.location.hash?.includes('session_id=')) {
      setLoading(false);
      return;
    }
    if (checkedRef.current) return;
    checkedRef.current = true;
    checkAuth();
  }, [checkAuth]);

  // Restore impersonation state when user data loads (handles page refresh during impersonation)
  useEffect(() => {
    if (!user || user._impersonating) return;
    const adminToken = sessionStorage.getItem('impersonation_admin_token');
    if (!adminToken) return;
    const adminUserStr = sessionStorage.getItem('impersonation_admin_user');
    if (adminUserStr) {
      try {
        const au = JSON.parse(adminUserStr);
        setUser(prev => prev ? { ...prev, _impersonating: true, _admin_email: au.email } : prev);
      } catch {}
    }
  }, [user?.id]); // runs when user ID becomes available after checkAuth

  const login = (token, userData) => {
    localStorage.setItem('vw_token', token);
    localStorage.setItem('vw_user', JSON.stringify(userData));
    setUser(userData);
  };

  const logout = () => {
    // Clear impersonation if active
    sessionStorage.removeItem('impersonation_admin_token');
    sessionStorage.removeItem('impersonation_admin_user');
    localStorage.removeItem('vw_token');
    localStorage.removeItem('vw_user');
    setUser(null);
  };

  const refreshUser = async () => {
    try {
      const res = await api.get('/auth/me');
      setUser(res.data);
      return res.data;
    } catch {
      return null;
    }
  };

  const startImpersonation = (token, targetUser) => {
    sessionStorage.setItem('impersonation_admin_token', localStorage.getItem('vw_token'));
    sessionStorage.setItem('impersonation_admin_user', JSON.stringify(user));
    localStorage.setItem('vw_token', token);
    setUser({ ...targetUser, _impersonating: true, _admin_email: user?.email });
  };

  const stopImpersonation = () => {
    const adminToken = sessionStorage.getItem('impersonation_admin_token');
    const adminUserStr = sessionStorage.getItem('impersonation_admin_user');
    if (adminToken) {
      localStorage.setItem('vw_token', adminToken);
      const adminUser = adminUserStr ? JSON.parse(adminUserStr) : null;
      if (adminUser) {
        localStorage.setItem('vw_user', adminUserStr);
        setUser(adminUser);
      }
    }
    sessionStorage.removeItem('impersonation_admin_token');
    sessionStorage.removeItem('impersonation_admin_user');
  };

  return (
    <AuthContext.Provider value={{
      user, loading, login, logout, refreshUser,
      startImpersonation, stopImpersonation,
      isImpersonating: !!user?._impersonating,
    }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
